import type { AlertStatus } from "./alert-status.types"
import type { TFunction } from "i18next"

import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "@/components/icons"

export function SeverityIcon({ severity }: { severity: string }) {
  switch (severity) {
    case "critical":
      return <AlertCircle className="text-destructive size-4" />
    case "warning":
      return <AlertTriangle className="text-warning size-4" />
    case "info":
    default:
      return <Info className="text-muted-foreground size-4" />
  }
}

export function StatusIndicator({
  status,
  criticalCount,
  warningCount,
  t,
}: {
  status: AlertStatus
  criticalCount: number
  warningCount: number
  t: TFunction
}) {
  switch (status) {
    case "action_required":
      return (
        <div className="bg-destructive/10 flex items-center gap-3 rounded-lg p-3">
          <div className="bg-destructive/15 flex size-10 items-center justify-center rounded-full">
            <AlertCircle className="text-destructive size-5" />
          </div>
          <div>
            <div className="text-destructive font-semibold">
              {t("adminDashboard.alerts.statusActionRequired", "Action Required")}
            </div>
            <div className="text-destructive/80 text-sm">
              {t("adminDashboard.alerts.statusCriticalCount", "{{count}} critical incidents", {
                count: criticalCount,
              })}
            </div>
          </div>
        </div>
      )
    case "attention":
      return (
        <div className="bg-warning/10 flex items-center gap-3 rounded-lg p-3">
          <div className="bg-warning/15 flex size-10 items-center justify-center rounded-full">
            <AlertTriangle className="text-warning size-5" />
          </div>
          <div>
            <div className="text-warning-foreground font-semibold">
              {t("adminDashboard.alerts.statusAttention", "Attention")}
            </div>
            <div className="text-warning-foreground/80 text-sm">
              {t("adminDashboard.alerts.statusWarningCount", "{{count}} warnings to review", {
                count: warningCount,
              })}
            </div>
          </div>
        </div>
      )
    case "all_clear":
    default:
      return (
        <div className="bg-success/10 flex items-center gap-3 rounded-lg p-3">
          <div className="bg-success/15 flex size-10 items-center justify-center rounded-full">
            <CheckCircle2 className="text-success size-5" />
          </div>
          <div>
            <div className="text-success font-semibold">
              {t("adminDashboard.alerts.statusAllClear", "All Clear")}
            </div>
            <div className="text-success/80 text-sm">
              {t("adminDashboard.alerts.statusNoCritical", "No critical or warning incidents")}
            </div>
          </div>
        </div>
      )
  }
}
